import { fetchJSON as fetchAPI } from './client';

export type ResilienceDashboard = {
  tenant_id: string;
  total_backups: number;
  verified_backups: number;
  active_restores: number;
  recovery_plans: number;
  last_drill_passed: boolean;
  replication_healthy: number;
  replication_total: number;
  current_rto_seconds: number;
  current_rpo_seconds: number;
  target_rto_seconds: number;
  target_rpo_seconds: number;
  integrity_pass_rate: number;
  generated_at: string;
  platform_health?: { module: string; status: string; latency_ms?: number }[];
};

export type BackupJob = {
  id: string;
  tenant_id: string;
  resource_type: string;
  mode: string;
  status: string;
  size_bytes: number;
  checksum?: string;
  encrypted: boolean;
  region?: string;
  started_at: string;
  completed_at?: string;
};

export type RestoreJob = {
  id: string;
  tenant_id: string;
  backup_id: string;
  status: string;
  target_region?: string;
  started_at: string;
  completed_at?: string;
};

export type RecoveryPlan = {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  type: string;
  rto_seconds: number;
  rpo_seconds: number;
  steps?: string[];
  automated: boolean;
  active: boolean;
};

export type DisasterDrill = {
  id: string;
  name: string;
  status: string;
  rto_achieved_seconds?: number;
  rpo_achieved_seconds?: number;
  passed: boolean;
  started_at: string;
};

export type ReplicationTask = {
  id: string;
  source_region: string;
  target_region: string;
  resource_type: string;
  status: string;
  lag_seconds: number;
  last_sync_at: string;
};

export type RTOMetrics = {
  tenant_id: string;
  target_seconds: number;
  current_seconds: number;
  last_drill_seconds: number;
  within_target: boolean;
};

export type RPOMetrics = {
  tenant_id: string;
  target_seconds: number;
  current_seconds: number;
  last_backup_lag_seconds: number;
  within_target: boolean;
};

const ROOT = import.meta.env.VITE_RESILIENCE_API ?? '/api/v1';

function fetchRoot<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(ROOT, path, init);
}

export const resilienceApi = {
  dashboard: () => fetchRoot<ResilienceDashboard>('/resilience/dashboard'),
  rto: () => fetchRoot<RTOMetrics>('/resilience/rto'),
  rpo: () => fetchRoot<RPOMetrics>('/resilience/rpo'),
  listBackups: () => fetchRoot<BackupJob[]>('/backups'),
  createBackup: (body: { resource_type: string; mode?: string }) =>
    fetchRoot<BackupJob>('/backups', { method: 'POST', body: JSON.stringify(body) }),
  restoreBackup: (id: string, body?: { target_region?: string; granular?: boolean }) =>
    fetchRoot<RestoreJob>(`/backups/${id}/restore`, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  listRecoveryPlans: () => fetchRoot<RecoveryPlan[]>('/recovery/plans'),
  runDrill: (body?: { plan_id?: string; name?: string }) =>
    fetchRoot<DisasterDrill>('/recovery/drill', { method: 'POST', body: JSON.stringify(body ?? {}) }),
  listReplication: () => fetchRoot<ReplicationTask[]>('/resilience/replication'),
  listIntegrity: () => fetchRoot<{ id: string; backup_id?: string; status: string; checksum_ok: boolean }[]>('/resilience/integrity'),
  listActivity: () => fetchRoot<{ id: string; action: string; message: string; created_at: string }[]>('/resilience/activity'),
};
